// web-frontend/src/components/common/LanguageSelector.jsx
// 🌐 [2026-01-21 신규] 글래스모피즘 다국어 선택 드롭다운 컴포넌트
// 🔥 헤더 및 사이드바에서 재사용 가능한 공통 컴포넌트
// 🔥 ProfilePage.jsx 로직 통합 - 백엔드 프로필 동기화 포함
// 🔥 템플릿 리터럴 구문 오류 수정 (+ 연산자 사용)

import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import useAppStore from '../../shared/stores/appStore';
import useAuthStore from '../../shared/stores/authStore';
import authService from '../../shared/services/authService';
import i18nService from '../../shared/services/i18n';
import logger from '../../shared/utils/logger';
import './LanguageSelector.css';

const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  
  const { language, setLanguage } = useAppStore();
  const { user, updateUser } = useAuthStore();
  
  // 🌐 지원 언어 목록 가져오기
  const languages = i18nService.getSupportedLanguages(); // ko, en, ja, zh

  // 🔥 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🔥 ESC 키로 드롭다운 닫기
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // 🌐 언어 변경 핸들러 (ProfilePage 로직 이식)
  const handleLanguageChange = async (newLang) => {
    if (newLang === language || isLoading) return;
    
    setIsLoading(true);
    
    try {
      // 1. 로그인 상태라면 백엔드 프로필 업데이트
      if (user) {
        try {
          await authService.updateProfile({ preferred_language: newLang });
          updateUser({ ...user, preferred_language: newLang });
          logger.debug('✅ [LanguageSelector] 백엔드 언어 동기화 성공:', newLang);
        } catch (err) {
          logger.warn('⚠️ [LanguageSelector] 백엔드 언어 동기화 실패 (로컬만 변경):', err);
        }
      }
      
      // 2. 전역 상태 및 i18n 서비스 변경
      await setLanguage(newLang);
      
      logger.debug('✅ [LanguageSelector] 언어 변경 완료:', newLang);
      setIsOpen(false);
      
    } catch (error) {
      logger.error('❌ [LanguageSelector] 언어 변경 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 🏷️ 현재 선택된 언어의 네이티브 이름 가져오기
  const currentLangLabel = languages.find(l => l.code === language)?.nativeName || 'Language';

  return (
    <div className="language-selector-container" ref={dropdownRef}>
      {/* 🔘 트리거 버튼 */}
      <button 
        className={'lang-trigger-btn ' + (isOpen ? 'active' : '')}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select Language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={isLoading}
      >
        <Globe size={18} className="lang-globe-icon" />
        <span className="lang-text">{language.toUpperCase()}</span>
        <ChevronDown 
          size={14} 
          className={'arrow-icon ' + (isOpen ? 'rotate' : '')} 
        />
      </button>

      {/* 📜 드롭다운 메뉴 (글래스모피즘) */}
      {isOpen && (
        <div 
          className="lang-dropdown-menu glass-effect"
          role="listbox"
          aria-label="Language selection"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={'lang-option ' + (language === lang.code ? 'selected' : '')}
              onClick={() => handleLanguageChange(lang.code)}
              role="option"
              aria-selected={language === lang.code}
              disabled={isLoading}
            >
              <span className="lang-flag">{lang.flag || ''}</span>
              <span className="lang-name">{lang.nativeName}</span>
              {language === lang.code && (
                <Check size={14} className="check-icon" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;